import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Checkbox, Dialog, DialogContent, OtpInput, Text,
} from '@datanomik/core-ui';
import { QuestionLine } from '@datanomik/core-ui/icons';

import type { MfaChallenge } from '../model/types';

const TOKEN_LENGTH = 6;

const HELP_ITEMS = [
  'Look for Datanomik in the authenticator app you used the first time you signed in — Google Authenticator, Microsoft Authenticator, Authy or similar.',
  'The code changes every 30 seconds. If it changes while you type, use the new one.',
  'Changed phones or lost access to the app? Contact Datanomik support to reset your verification.',
];

interface MfaTokenFormProps {
  challenge: MfaChallenge;
  busy: boolean;
  onSubmit: (token: string, trustDevice: boolean) => void;
}

function CodeHelpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        icon={<QuestionLine />}
        title={t("Can't find the code?")}
        footer={<Button className="w-full" onClick={() => onOpenChange(false)}>{t('Close')}</Button>}
      >
        <ul className="flex list-disc flex-col gap-2 pl-5">
          {HELP_ITEMS.map((item) => (
            <li key={item}>
              <Text variant="body-sm" tone="muted">{t(item)}</Text>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export function MfaTokenForm({ challenge, busy, onSubmit }: MfaTokenFormProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);

  const complete = token.length === TOKEN_LENGTH;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (complete) onSubmit(token, trustDevice);
      }}
      className="flex flex-col gap-6"
    >
      <Text variant="body-sm" tone="muted" className="-mt-3 text-center">
        {challenge.qrCode
          ? t('Scan the QR code with an authenticator app, such as Google Authenticator, Microsoft Authenticator or Authy, then type the 6-digit code it shows.')
          : t('Type the 6-digit code shown for Datanomik. It is not sent by email.')}
      </Text>

      {challenge.qrCode && (
        <img
          src={challenge.qrCode}
          alt={t('Authentication QR code')}
          className="size-40 self-center rounded-lg bg-white p-2"
        />
      )}

      <OtpInput
        value={token}
        onChange={setToken}
        length={TOKEN_LENGTH}
        autoFocus
        label={t('Code from the authenticator app')}
        onComplete={(value) => { if (!busy) onSubmit(value, trustDevice); }}
      />

      <div className="flex flex-col gap-4">
        <Checkbox
          label={t('Trust this device')}
          checked={trustDevice}
          onCheckedChange={(value) => setTrustDevice(value === true)}
        />
        <Button type="submit" className="w-full" loading={busy} disabled={!complete}>
          {t('Continue')}
        </Button>
        {!challenge.qrCode && (
          <Button
            type="button"
            variant="link"
            className="self-center text-muted-foreground hover:text-foreground"
            onClick={() => setHelpOpen(true)}
          >
            {t("Can't find the code?")}
          </Button>
        )}
      </div>

      <CodeHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </form>
  );
}
